import {CookieStorage} from 'cookie-storage';

/**
 * Persists campaign and partner attribution to first-party cookies.
 *
 * These cookie names were previously written by GTM, which is now gated on analytics consent —
 * so for anyone who has not accepted the banner they never exist, and the cross-site fallback
 * silently fails. A visitor landing on the marketing site with `?utm_source=` and then following
 * a CTA that drops the query string reaches the walkthrough with nothing, and `logLead` records
 * them as `direct_traffic`. Paid channels under-report and nothing errors.
 *
 * Writing them here instead is not analytics: it carries a visitor's own referral through our own
 * funnel so the lead record says where it came from. That is submission and service data, the same
 * basis on which `logLead` already reads `mediaSource` without a consent check, and the same
 * category as the `amplifyUserUuid` cookie and the session state we persist for resume.
 *
 * Deliberately free of app imports so it can be copied to amplify-website-v2-frontend, which is
 * where most of these visits actually land. Writing it in only one of the two repos does not close
 * the gap.
 */

/** Cookie name by URL parameter. Names must match what RouterHoc and logLead already read. */
const ATTRIBUTION_COOKIES: Readonly<Record<string, string>> = {
    utm_campaign: 'mediaCampaign',
    utm_medium: 'mediaMedium',
    utm_source: 'mediaSource',
    utm_content: 'mediaContent',
    utm_adgroup: 'mediaAdgroup',
    partner_user_id: 'mediaPartnerUserId',
};

/** A conventional attribution window. Long enough to survive a considered purchase, not forever. */
const ATTRIBUTION_WINDOW_DAYS = 90;

/**
 * Writes any attribution parameter present in `params` to its cookie.
 *
 * Only URL-sourced values are written. Re-writing a value that came from the cookie would extend
 * its expiry on every visit and make the first touch stick indefinitely.
 */
export const captureMarketingAttribution = (params: URLSearchParams | null): void => {
    if (!params) return;

    try {
        const cookies = new CookieStorage();
        const expires = new Date(Date.now() + ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000);

        Object.entries(ATTRIBUTION_COOKIES).forEach(([param, cookieName]) => {
            const value = params.get(param);
            if (!value) return;

            cookies.setItem(cookieName, value, {
                path: '/',
                // Shared across quotes./www./portal. so a CTA between them keeps the attribution.
                ...(process.env.NODE_ENV === 'production' ? {domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN} : {}),
                secure: true,
                sameSite: 'Lax',
                expires,
            });
        });
    } catch {
        // Blocked cookies only cost attribution, so never let it break the funnel.
    }
};
